// src/utils/apiUtils.js (or any path you prefer)
import axios from 'axios';
import { CONSTANTS } from '../CONSTANTS';

/**
 * Axios config for a JSON call carrying the caller's bearer token.
 *
 * `useGetToken` already returns a "Bearer "-prefixed string, but not every
 * caller gets its token from there, so the prefix is added only when it is
 * missing rather than assumed either way.
 */
export const authConfig = (token: string | undefined) => ({
  headers: {
    'Content-Type': 'application/json',
    Authorization: token?.startsWith('Bearer ') ? token : `Bearer ${token}`,
  },
});

/**
 * The message worth showing the admin from a failed API call.
 *
 * Several endpoints answer with a category in `message` and the part that says
 * what to actually do in `errors` or `error`. Custom field failures are the
 * clearest case: `message` is "Custom field validation failed" for every one of
 * them, while `errors[].message` names the field, the value and the clash. So
 * the specific text is preferred and the category is the fallback, not the
 * other way round.
 *
 * Several field errors are joined rather than truncated - a save refused for
 * two reasons should not read as though it were refused for one.
 */
export const apiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  if (!data) return fallback;

  const details: string[] = Array.isArray(data.errors)
    ? data.errors
        .map((item: any) => (typeof item === 'string' ? item : item?.message))
        .filter((text: unknown): text is string => typeof text === 'string' && !!text.trim())
    : [];
  if (details.length > 0) return details.join(' ');

  return data.error || data.message || fallback;
};

/**
 * Whether a caller holds a permission, by the same rules the API applies.
 *
 * Mirrors `hasPermission` in the backend's permissionMiddleware: the global '*'
 * grants everything, an exact match grants itself, and a 'namespace:resource:*'
 * entry grants every action on that resource. Kept in step deliberately - a
 * screen that hid a button the server would have allowed, or offered one it
 * refuses, is worse than either rule on its own.
 *
 * Permissions come from GET /v1/me and originate in ad_group_permissions, so
 * nothing here needs to know which AD groups exist.
 */
export const hasPermission = (
  permissions: string[] | undefined,
  required: string
): boolean => {
  if (!permissions || permissions.length === 0) return false;
  if (permissions.includes('*')) return true;
  if (permissions.includes(required)) return true;
  const parts = required.split(':');
  if (parts.length === 3) {
    return permissions.includes(`${parts[0]}:${parts[1]}:*`);
  }
  return false;
};

export const logUserAction = async (token: string | undefined, email: string, action: string) => {
  const formData = { action, userId: email };

  if (action === CONSTANTS.USER_ACTION.LOGIN && localStorage.getItem('login') === 'true') {
    console.log('User is already logged in. Skipping login action.');
    return { message: 'User already logged in' };

  }

  try {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: token?.startsWith('Bearer ') ? token : `Bearer ${token}`,
      },
    };
    const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/logActivity`,
      formData,
      config
    );



    console.log('POST request successful:', response?.data);
    console.log('response status :', response?.status);
        // Clear local storage for login
    if (response?.status !== 200 || !response?.data?.message.includes('success')) {
      console.log('%cFailed to log activity:', 'color: white; background-color: crimson; font-weight: bold; padding: 4px; border-radius: 4px;',`${action} activity logging failed.`);
      return
    }
    switch (action) {
      case 'login':
         localStorage.setItem('login', 'true');
        break;
      case 'logout':
        localStorage.removeItem('login');
        break;
      default:
        break;
    }
    console.log('%cActivity logged', 'color: white; background-color: green; font-weight: bold; padding: 4px; border-radius: 4px;',`${action} activity logged successfully.`);
    

  } catch (error) {
    console.error('Error sending POST request:', error);
  }
};
